import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Divider,
  DropdownSelect,
  IconButton,
  Menu,
  Switch,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconCloseFilled, IconInfoFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { PlacementSource, SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { OPTIMIZER_LIST } from './rabDeployment';
import { SmartAssignActivationModal } from './SmartAssignActivationModal';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';
import { getCurrentPlacementSource } from './utils/cacheHelpers';

import type { OptimizerId } from './rabDeployment';

const INFO_ICON = (
  <IconButton
    title="Info"
    size="sm"
    emphasis="bare"
    shape="circle"
    css={{ color: 'icon.muted', width: 'icon.sm', height: 'icon.sm' }}
    icon={<IconInfoFilled />}
  />
);

export function SmartAssignHeader() {
  const ctx = useAutoSpotlight();
  const queryClient = useQueryClient();
  const [activationOpen, setActivationOpen] = useState(false);

  if (!ctx) {
    return null;
  }

  const {
    autoCount,
    availableCount,
    batchExclude,
    batchInclude,
    batchPin,
    batchUnexclude,
    batchUninclude,
    batchUnpin,
    clearSelection,
    excludedIds,
    isDisablingSmartAssign,
    isOverviewLoaded,
    isSelectionMode,
    licenses,
    onChangeOptimizer,
    optimizer,
    pinnedCount,
    pinnedIds,
    requestDisableSmartAssign,
    selectedIds,
    smartConfigMode,
    spotlightedCount,
    hasZramManageableListings,
    toggleSelectionMode,
  } = ctx;

  const smartAssignEnabled = smartConfigMode !== null;
  const inSpotlight = spotlightedCount;
  // Only feed-managed listings → nothing for Smart Assign to manage; drives the explanatory tooltip.
  const feedManagedBlocked = !smartAssignEnabled && isOverviewLoaded && !hasZramManageableListings;
  // Block enabling for the feed-managed case, and until the overview loads so the real value can't be pre-empted.
  // Never blocks disabling.
  const enableBlocked = !smartAssignEnabled && (!isOverviewLoaded || !hasZramManageableListings);

  const handleToggle = () => {
    if (!smartAssignEnabled) {
      if (enableBlocked) {
        return;
      }
      setActivationOpen(true);
      return;
    }
    requestDisableSmartAssign();
  };

  const options = OPTIMIZER_LIST.map((o) => ({ label: o.label, value: o.id, meta: o.description }));
  const selectedCount = selectedIds.size;
  const selectedAliases = Array.from(selectedIds);

  const isPinned = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.PINNED : pinnedIds.has(id);
  };
  const isExcluded = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.EXCLUDED : excludedIds.has(id);
  };
  // No optimistic set to fall back on, so before the per-listing queries load every listing reads as not opted in.
  const isIncluded = (id: string) => getCurrentPlacementSource(queryClient, id) === PlacementSource.INCLUDED;

  const anyPinned = selectedAliases.some(isPinned);
  const allPinned = selectedAliases.length > 0 && selectedAliases.every(isPinned);
  const anyExcluded = selectedAliases.some(isExcluded);
  const allExcluded = selectedAliases.length > 0 && selectedAliases.every(isExcluded);
  const anyIncluded = selectedAliases.some(isIncluded);
  const allIncluded = selectedAliases.length > 0 && selectedAliases.every(isIncluded);
  const overLimit = isOverviewLoaded && selectedCount > availableCount;

  // Only reachable with Smart Assign on, so the mode is never null here. The action the mode already applies by
  // default is greyed rather than hidden: that is what conveys the mode.
  const bulkActions: Array<{ label: string; onSelect: () => void; disabled?: boolean }> = [
    ...(!anyPinned ? [{ label: 'Pin', onSelect: () => batchPin(selectedAliases), disabled: overLimit }] : []),
    ...(allPinned ? [{ label: 'Unpin', onSelect: () => batchUnpin(selectedAliases) }] : []),
    ...(!anyIncluded
      ? [
          {
            label: 'Smart Assign',
            onSelect: () => batchInclude(selectedAliases),
            disabled: smartConfigMode === SmartConfigMode.ALL_INCLUDED,
          },
        ]
      : []),
    ...(allIncluded ? [{ label: 'Remove from Smart Assign', onSelect: () => batchUninclude(selectedAliases) }] : []),
    ...(!anyExcluded
      ? [
          {
            label: 'Exclude',
            onSelect: () => batchExclude(selectedAliases),
            disabled: smartConfigMode === SmartConfigMode.ALL_EXCLUDED,
          },
        ]
      : []),
    ...(allExcluded ? [{ label: 'Unexclude', onSelect: () => batchUnexclude(selectedAliases) }] : []),
  ];

  return (
    <Card data-testid="smart-assign-header" css={{ marginBlock: '400', padding: '500' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '200' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '300' }}>
          <Text textStyle="heading-sm">{COPY.product.spotlight}</Text>
          <Box
            css={{
              display: 'flex',
              alignItems: 'baseline',
              paddingLeft: '300',
              borderLeft: '1px solid {colors.border.subtle}',
            }}
          >
            <Text
              textStyle={availableCount > 0 ? 'body-sm-bold' : 'body-sm'}
              asChild
              css={{ color: availableCount > 0 ? '{colors.text.accent.blue.strong}' : '{colors.text.subtle}' }}
            >
              <span>
                {availableCount} Available {availableCount === 1 ? 'placement' : 'placements'}
              </span>
            </Text>
            <Text textStyle="body-sm" asChild css={{ color: '{colors.text.subtle}' }}>
              <span>
                {smartAssignEnabled
                  ? ` · ${pinnedCount} Pinned · ${autoCount} Smart Assign`
                  : ` · ${inSpotlight} in Spotlight`}
              </span>
            </Text>
          </Box>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
          <Text textStyle="body-sm-bold">{COPY.smartAssign.toggleLabel}</Text>
          <Switch
            controlId="smart-assign-toggle"
            aria-label={COPY.smartAssign.toggleLabel}
            checked={smartAssignEnabled}
            disabled={isDisablingSmartAssign || enableBlocked}
            onChange={handleToggle}
          />
          <Tooltip
            content={feedManagedBlocked ? COPY.smartAssign.feedManagedTooltip : COPY.smartAssign.tooltip}
            placement="top-end"
            trigger={INFO_ICON}
          />
        </Box>
      </Box>

      {smartAssignEnabled && (
        <>
          <Divider css={{ marginBlock: '400' }} />

          <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexWrap: 'wrap' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexShrink: 0 }}>
              <Tooltip content={COPY.tooltips.optimizeBy} trigger={INFO_ICON} />
              <Text textStyle="body-sm-bold" css={{ color: '{colors.text.neutral}' }}>
                Optimize by
              </Text>
              <DropdownSelect
                value={optimizer}
                options={options}
                onChange={(value) => onChangeOptimizer((Array.isArray(value) ? value[0] : value) as OptimizerId)}
                showLabelForValue
                size="sm"
                css={{ width: '220px' }}
              />
            </Box>

            {isSelectionMode ? (
              <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginInlineStart: 'auto' }}>
                <Text textStyle="body-sm">{selectedCount} selected</Text>
                <IconButton
                  title="Clear selection"
                  size="sm"
                  emphasis="bare"
                  shape="circle"
                  icon={<IconCloseFilled />}
                  onClick={clearSelection}
                />
                {selectedCount > 0 && bulkActions.length > 0 && (
                  <Menu
                    trigger={
                      <Button size="sm" emphasis="outlined">
                        Action ▾
                      </Button>
                    }
                    content={bulkActions.map(({ label, onSelect, disabled }) => (
                      <Menu.Item key={label} onClick={disabled ? undefined : onSelect} disabled={disabled}>
                        <Menu.ItemLabel>{label}</Menu.ItemLabel>
                      </Menu.Item>
                    ))}
                  />
                )}
              </Box>
            ) : (
              <Button size="sm" emphasis="outlined" onClick={toggleSelectionMode} css={{ marginInlineStart: 'auto' }}>
                Multi-select
              </Button>
            )}
          </Box>
        </>
      )}

      {activationOpen && (
        <SmartAssignActivationModal
          open
          onClose={() => setActivationOpen(false)}
          inUse={inSpotlight}
          total={licenses}
        />
      )}
    </Card>
  );
}

export default SmartAssignHeader;
